import { useState } from "react";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtDateTime, TX_STATUS, getDefaultCurrency } from "@/lib/format";
import { PageHeader, StatusBadge, Empty, DemoBanner, StatCard } from "@/components/kit";

const DepositForm = ({ onDone }) => {
  const [f, setF] = useState({ amount: "", currency: getDefaultCurrency(), method: "pix" });
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/me/deposits", { ...f, amount: Number(f.amount) }); toast.success(`Depósito solicitado: ${data.reference}`); setF({ ...f, amount: "" }); onDone(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="deposit-form">
      <div className="space-y-2"><Label>Valor</Label><Input type="number" min="10" step="0.01" required className="h-12 num" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="deposit-amount-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Moeda</Label>
          <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}><SelectTrigger className="h-12" data-testid="deposit-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["USD", "BRL", "EUR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-2"><Label>Método</Label>
          <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}><SelectTrigger className="h-12" data-testid="deposit-method-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="pix">PIX</SelectItem><SelectItem value="transferencia">Transferência</SelectItem><SelectItem value="cartao">Cartão</SelectItem></SelectContent></Select></div>
      </div>
      <p className="text-xs text-muted-foreground">Limites: US$ 10 a US$ 50.000 por solicitação. Valores acima de US$ 10.000 passam por análise de risco. O valor só é creditado após confirmação verificada do pagamento.</p>
      <Button className="h-12 w-full rounded-xl" data-testid="deposit-submit-button"><ArrowDownToLine className="mr-2 h-4 w-4" />Solicitar depósito</Button>
    </form>
  );
};

const WithdrawForm = ({ onDone, user }) => {
  const [f, setF] = useState({ amount: "", currency: getDefaultCurrency(), destination: "", password: "", totp_code: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/me/withdrawals", { ...f, amount: Number(f.amount), totp_code: f.totp_code || null });
      toast.success(`Saque solicitado: ${data.reference}`); setF({ ...f, amount: "", password: "", totp_code: "" }); onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="withdraw-form">
      <div className="space-y-2"><Label>Valor</Label><Input type="number" min="1" step="0.01" required className="h-12 num" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="withdraw-amount-input" /></div>
      <div className="space-y-2"><Label>Destino (chave PIX ou IBAN/conta)</Label><Input required className="h-12" value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} data-testid="withdraw-destination-input" /></div>
      <div className="space-y-2"><Label>Confirme sua senha</Label><Input type="password" required className="h-12" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="withdraw-password-input" /></div>
      {user.totp_enabled && <div className="space-y-2"><Label>Código 2FA</Label><Input inputMode="numeric" maxLength={6} required className="h-12 num" value={f.totp_code} onChange={(e) => setF({ ...f, totp_code: e.target.value })} data-testid="withdraw-2fa-input" /></div>}
      <p className="flex gap-2 text-xs text-muted-foreground"><ShieldAlert className="h-4 w-4 shrink-0" />O valor fica bloqueado até a análise. Limite diário de US$ 20.000. Exige e-mail verificado.</p>
      <Button variant="outline" className="h-12 w-full rounded-xl" data-testid="withdraw-submit-button"><ArrowUpFromLine className="mr-2 h-4 w-4" />Solicitar saque</Button>
    </form>
  );
};

export default function WalletPage() {
  const { user } = useAuth();
  const [tx, setTx] = useState([]);
  const [b, setB] = useState(null);
  const load = () => Promise.all([api.get("/me/transactions"), api.get("/me/dashboard")]).then(([t, d]) => { setTx(t.data); setB(d.data.balances); });
  usePoll(() => load().catch(() => {}), 10000);
  const cancel = async (id) => { try { await api.post(`/me/transactions/${id}/cancel`); toast.success("Solicitação cancelada"); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="wallet-page">
      <PageHeader eyebrow="Financeiro" title="Depósitos e saques" desc="Acompanhe cada solicitação do registro até a confirmação. Transferências pendentes nunca entram no saldo disponível." />
      <div className="mb-6"><DemoBanner text="Modo demonstração: as solicitações são analisadas pela equipe administrativa e movimentam apenas saldo virtual. Integração com provedor de pagamento autorizado preparada via webhook assinado." /></div>
      {b && <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="wallet-available" label="Disponível" value={fmtMoney(b.available)} tone="up" />
        <StatCard testid="wallet-blocked-withdrawals" label="Bloqueado para saques" value={fmtMoney(b.blocked_withdrawals)} />
        <StatCard testid="wallet-deposits" label="Depósitos confirmados" value={fmtMoney(b.deposits_confirmed)} />
        <StatCard testid="wallet-withdrawn" label="Saques concluídos" value={fmtMoney(b.withdrawals_completed)} />
      </div>}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="surface p-5 md:p-7 lg:col-span-4">
          <Tabs defaultValue="deposit">
            <TabsList className="grid grid-cols-2 w-full mb-5"><TabsTrigger value="deposit" data-testid="tab-deposit">Depositar</TabsTrigger><TabsTrigger value="withdraw" data-testid="tab-withdraw">Sacar</TabsTrigger></TabsList>
            <TabsContent value="deposit"><DepositForm onDone={load} /></TabsContent>
            <TabsContent value="withdraw"><WithdrawForm onDone={load} user={user} /></TabsContent>
          </Tabs>
        </div>
        <div className="surface p-5 md:p-7 lg:col-span-8 overflow-x-auto" data-testid="transactions-table">
          <div className="eyebrow mb-4">Histórico de transações</div>
          {tx.length === 0 ? <Empty text="Nenhuma solicitação." /> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left eyebrow"><th className="py-2">Referência</th><th>Tipo</th><th className="text-right">Valor</th><th>Status</th><th>Data</th><th /></tr></thead>
              <tbody>{tx.map((t) => (
                <tr key={t.id} className="border-t border-border" data-testid={`tx-row-${t.id}`}>
                  <td className="py-3 num text-xs">{t.reference}{t.external_ref && <div className="text-muted-foreground">Ext: {t.external_ref}</div>}</td>
                  <td>{t.type === "deposit" ? "Depósito" : "Saque"}<div className="text-xs text-muted-foreground">{t.method || t.destination}</div></td>
                  <td className="text-right num font-semibold">{fmtMoney(t.amount, t.currency)}</td>
                  <td><StatusBadge map={TX_STATUS} value={t.status} testid={`tx-status-${t.id}`} /></td>
                  <td className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</td>
                  <td>{t.status === "requested" && <Button size="sm" variant="ghost" onClick={() => cancel(t.id)} data-testid={`tx-cancel-${t.id}`}>Cancelar</Button>}</td>
                </tr>))}</tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
